"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FiPlus, FiTrash2, FiUpload, FiFolder } from "react-icons/fi";
import { LuAudioLines } from "react-icons/lu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/shadcn/dropdown-menu";
import { useModal } from "@/contexts/ModalContext";
import AssetsBrowserModal from "@/components/modals/AssetsBrowserModal";
import {
  ASSET_DRAG_MIME,
  ASSET_MEDIA_PREFIX,
} from "@/components/AssetsEditor/constants";
import { toast } from "sonner";
import {
  makePendingUpload,
  pickerDisplayUrl,
  pickerThumbnailUrl,
} from "./pickerValues";

// The string values predate this module (they started in the storyboard) and
// are kept as-is so drags between old and new drop targets keep matching.
export const GENERATION_DRAG_TYPE =
  "application/x-kagami-storyboard-generation";
export const GENERATION_MEDIA_PREFIX = "application/x-kagami-gen-";

/**
 * @input "video"
 * @output "application/x-kagami-gen-video"
 */
export function generationMediaDragType(mediaType) {
  return `${GENERATION_MEDIA_PREFIX}${mediaType}`;
}

function getGenerationDraggedMediaType(types) {
  const match = types.find((t) => t.startsWith(GENERATION_MEDIA_PREFIX));
  return match ? match.slice(GENERATION_MEDIA_PREFIX.length) : null;
}

/**
 * Returns every `mediaType` advertised on the current asset drag. Multi-asset
 * drags from AssetsEditor set one `assetMediaDragType(mt)` marker per unique
 * mediaType in the selection (see `AssetItem.handleDragStart`), so reading
 * them all lets a typed drop target accept a drag that contains *some*
 * matching asset and filter the rest at drop time.
 */
function getAssetDraggedMediaTypes(types) {
  return types
    .filter((t) => t.startsWith(ASSET_MEDIA_PREFIX))
    .map((t) => t.slice(ASSET_MEDIA_PREFIX.length));
}

function hasMatchingFileItem(dataTransfer, mediaType) {
  const items = [...(dataTransfer?.items || [])];
  if (items.length === 0) return true;
  return items.some(
    (it) =>
      it.kind === "file" && (!it.type || it.type.startsWith(`${mediaType}/`)),
  );
}

/**
 * Compact square asset picker. Supports:
 *  - Drop files (stored locally; actual upload deferred until Generate)
 *  - Drop a generation asset (URL captured directly)
 *  - Click empty square to open a menu: Upload (file picker, stored locally as
 *    a pending file) or Browse (pick an existing asset via AssetsBrowserModal)
 *  - Click a populated square to preview the asset in a modal
 *  - Click X to clear
 *
 * `asset` can be either a URL string (already-uploaded asset) or a pending
 * upload wrapper produced by `makePendingUpload`.
 */
function MiniAssetPicker({
  asset,
  mediaType = "image",
  onChange,
  onAdd,
  onPreview,
  sizeClass = "w-16 h-16",
  disabled = false,
  multiSelect = false,
  dragHandleProps,
  resolveAssetById,
  onAssetDrop,
}) {
  const { openModal } = useModal();
  const inputId = useMemo(
    () => `mini-upload-${Math.random().toString(36).slice(2, 10)}`,
    [],
  );
  const [isDragOver, setIsDragOver] = useState(false);
  // Global drag state, used to grey-out non-matching pickers while a relevant
  // drag is in progress.
  const [isDragActive, setIsDragActive] = useState(false);
  const [isDragTypeMatch, setIsDragTypeMatch] = useState(false);
  const dragCounter = useRef(0);

  // Whether to accept a drag based purely on the dataTransfer types. We
  // intentionally don't peek at file MIME types here: some browsers don't
  // expose them during dragenter/dragover, so we accept any file drag and
  // filter at drop time, so file drops never silently get rejected on the
  // dragover phase.
  const acceptsDrag = useCallback(
    (e) => {
      const types = [...e.dataTransfer.types];
      if (types.includes(GENERATION_DRAG_TYPE)) {
        return getGenerationDraggedMediaType(types) === mediaType;
      }
      if (resolveAssetById && types.includes(ASSET_DRAG_MIME)) {
        const dragged = getAssetDraggedMediaTypes(types);
        if (dragged.length === 0) return true;
        if (dragged.includes(mediaType)) return true;
        // Multi-type drags get redistributed across the whole panel, so
        // any picker can accept the drop even when its own type isn't in
        // the selection (the drop handler routes each asset to the best
        // input by mediaType).
        return dragged.length > 1 && !!onAssetDrop;
      }
      if (types.includes("Files")) {
        // Best-effort match for the greyed-out non-match styling; falls
        // back to `true` when item types aren't readable yet.
        return hasMatchingFileItem(e.dataTransfer, mediaType);
      }
      return false;
    },
    [mediaType, resolveAssetById, onAssetDrop],
  );

  // Document-level drag tracking → drives the greyed-out non-match state.
  useEffect(() => {
    let scheduled = false;
    let resetTimeout = null;
    const resetDrag = () => {
      setIsDragActive(false);
      setIsDragTypeMatch(false);
      setIsDragOver(false);
      dragCounter.current = 0;
      if (resetTimeout) {
        clearTimeout(resetTimeout);
        resetTimeout = null;
      }
    };
    const onGlobalDragOver = (e) => {
      const types = [...e.dataTransfer.types];
      const isGen = types.includes(GENERATION_DRAG_TYPE);
      const isAsset = !!resolveAssetById && types.includes(ASSET_DRAG_MIME);
      const isFile = types.includes("Files");
      if (!isGen && !isAsset && !isFile) return;
      let match = false;
      if (isGen) match = getGenerationDraggedMediaType(types) === mediaType;
      else if (isAsset) {
        const dragged = getAssetDraggedMediaTypes(types);
        if (dragged.length === 0) match = true;
        else if (dragged.includes(mediaType)) match = true;
        // Multi-type asset drags will be redistributed at the panel
        // level, so visually invite drops on *every* picker (no
        // greyed-out state) when there's an onAssetDrop handler.
        else match = dragged.length > 1 && !!onAssetDrop;
      } else if (isFile) match = hasMatchingFileItem(e.dataTransfer, mediaType);

      if (resetTimeout) clearTimeout(resetTimeout);
      // Auto-reset shortly after the last dragover; covers cases where
      // dragend/drop don't fire (drop outside window, etc.).
      resetTimeout = setTimeout(resetDrag, 300);
      if (!scheduled) {
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          setIsDragActive(true);
          setIsDragTypeMatch(match);
        });
      }
    };
    // Capture phase so a local handler's stopPropagation (e.g. on the
    // currently-hovered picker) doesn't prevent us from seeing the drag.
    document.addEventListener("dragover", onGlobalDragOver, true);
    document.addEventListener("dragend", resetDrag, true);
    document.addEventListener("drop", resetDrag, true);
    return () => {
      document.removeEventListener("dragover", onGlobalDragOver, true);
      document.removeEventListener("dragend", resetDrag, true);
      document.removeEventListener("drop", resetDrag, true);
      if (resetTimeout) clearTimeout(resetTimeout);
    };
  }, [mediaType, resolveAssetById, onAssetDrop]);

  const handleDragEnter = useCallback(
    (e) => {
      if (!acceptsDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current += 1;
      setIsDragOver(true);
    },
    [acceptsDrag],
  );

  const handleDragOver = useCallback(
    (e) => {
      if (!acceptsDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      // Asset-grid drags use effectAllowed='copyMove'; using 'move' here keeps
      // the cursor consistent with the rest of the grid. Generation/file drags
      // are always 'copy'.
      const types = [...e.dataTransfer.types];
      e.dataTransfer.dropEffect = types.includes(ASSET_DRAG_MIME)
        ? "move"
        : "copy";
    },
    [acceptsDrag],
  );

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragOver(false);
    }
  }, []);

  /**
   * Filters files by mediaType and emits them as pending uploads. Uses
   * `onAdd(values[])` when `multiSelect` is set (so the elements-row "add"
   * picker can append several pickers at once), otherwise emits the first
   * matching file via `onChange`.
   */
  const emitFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []);
      const valid = files.filter((f) =>
        (f?.type || "").startsWith(`${mediaType}/`),
      );
      const skipped = files.length - valid.length;
      if (skipped > 0) {
        toast.warning(
          `Skipped ${skipped} file${skipped === 1 ? "" : "s"} — expected ${mediaType}`,
        );
      }
      if (valid.length === 0) return;
      if (multiSelect) {
        const pendings = valid.map((f) => makePendingUpload(f, mediaType));
        if (onAdd) onAdd(pendings);
        else onChange?.(pendings[0]);
      } else {
        onChange?.(makePendingUpload(valid[0], mediaType));
      }
    },
    [mediaType, multiSelect, onAdd, onChange],
  );

  const handleDrop = useCallback(
    (e) => {
      if (!acceptsDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragOver(false);
      const types = [...e.dataTransfer.types];
      if (types.includes(GENERATION_DRAG_TYPE)) {
        const raw = e.dataTransfer.getData(GENERATION_DRAG_TYPE);
        if (!raw) return;
        try {
          const payload = JSON.parse(raw);
          if (payload?.url && payload?.type === mediaType) {
            onChange?.(
              payload.thumbnail_url
                ? { url: payload.url, thumbnail_url: payload.thumbnail_url }
                : payload.url,
            );
          }
        } catch {
          /* ignore */
        }
        return;
      }
      if (resolveAssetById && types.includes(ASSET_DRAG_MIME)) {
        try {
          const ids = JSON.parse(
            e.dataTransfer.getData(ASSET_DRAG_MIME) || "[]",
          );
          const list = Array.isArray(ids) ? ids : [];
          const dragged = list
            .map((id) => resolveAssetById(id))
            .filter(Boolean);
          // Any drag carrying more than one asset is routed through the
          // panel-level distribution handler (see `distributeAssetsToInputs`
          // in `AiModelInput`), so each asset ends up in the best input
          // for its mediaType (e.g. images → elements, video → video_elements)
          // regardless of which picker physically received the drop.
          if (dragged.length > 1 && onAssetDrop) {
            onAssetDrop(dragged);
            return;
          }
          const matching = dragged.filter(
            (a) => a?.mediaType === mediaType && a?.url,
          );
          if (matching.length === 0) {
            if (dragged.length > 0) {
              const got =
                [...new Set(dragged.map((a) => a.mediaType))]
                  .filter(Boolean)
                  .join(", ") || "unknown";
              toast.error(`Expected ${mediaType}, got ${got}`);
            }
            return;
          }
          const toValue = (a) =>
            a.thumbnail_url
              ? { url: a.url, thumbnail_url: a.thumbnail_url }
              : a.url;
          if (multiSelect) {
            const value = toValue(matching[0]);
            if (onAdd) onAdd([value]);
            else onChange?.(value);
          } else {
            onChange?.(toValue(matching[0]));
          }
        } catch {
          /* ignore */
        }
        return;
      }
      emitFiles(e.dataTransfer.files);
    },
    [
      acceptsDrag,
      mediaType,
      onChange,
      onAdd,
      multiSelect,
      resolveAssetById,
      onAssetDrop,
      emitFiles,
    ],
  );

  const handleUploadClick = useCallback(() => {
    if (disabled) return;
    // Defer so the dropdown finishes closing / restoring focus before the
    // native file dialog opens (otherwise some browsers swallow the click).
    setTimeout(() => document.getElementById(inputId)?.click(), 0);
  }, [disabled, inputId]);

  /**
   * Emits already-uploaded asset records (from AssetsBrowserModal) as picker
   * values, mirroring `emitFiles` but for remote assets. Rich
   * `{ url, thumbnail_url }` values are produced so thumbnails render.
   */
  const emitAssets = useCallback(
    (assetList) => {
      const valid = (assetList || []).filter(
        (a) => a?.mediaType === mediaType && a?.url,
      );
      if (valid.length === 0) return;
      const toValue = (a) =>
        a.thumbnail_url
          ? { url: a.url, thumbnail_url: a.thumbnail_url }
          : a.url;
      if (multiSelect) {
        const values = valid.map(toValue);
        if (onAdd) onAdd(values);
        else onChange?.(values[0]);
      } else {
        onChange?.(toValue(valid[0]));
      }
    },
    [mediaType, multiSelect, onAdd, onChange],
  );

  const handleBrowseClick = useCallback(async () => {
    if (disabled) return;
    const result = await openModal(
      <AssetsBrowserModal mediaType={mediaType} multiSelect={multiSelect} />,
    );
    if (!result) return;
    emitAssets(Array.isArray(result) ? result : [result]);
  }, [disabled, openModal, mediaType, multiSelect, emitAssets]);

  const handleFileChange = useCallback(
    (e) => {
      // Snapshot into an array *before* clearing the input: `e.target.files`
      // is a live FileList, and resetting `e.target.value` empties that same
      // object, so reading it after the reset would yield nothing.
      const files = Array.from(e.target.files || []);
      e.target.value = "";
      emitFiles(files);
    },
    [emitFiles],
  );

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onChange?.(null);
    },
    [onChange],
  );

  const handlePreviewClick = useCallback(
    (e) => {
      e.stopPropagation();
      onPreview?.(asset, mediaType);
    },
    [onPreview, asset, mediaType],
  );

  const filled = !!asset;
  const displayUrl = pickerDisplayUrl(asset);
  const videoThumbnail = pickerThumbnailUrl(asset);

  const pickerSquare = (
    <div
      className={`relative shrink-0 ${sizeClass} rounded-md border bg-background overflow-hidden flex items-center justify-center select-none transition-opacity outline-hidden focus:outline-hidden focus-visible:outline-hidden
                ${filled ? "border-solid border-secondary" : "border-dashed border-secondary cursor-pointer hover:border-primary hover:text-primary"}
                ${isDragOver ? "border-primary border-solid bg-primary/10 ring-1 ring-primary/30" : ""}
                ${isDragActive && !isDragTypeMatch ? "opacity-30" : ""}
                ${filled ? "cursor-pointer" : ""}
            `}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={filled ? handlePreviewClick : undefined}
      {...(filled ? dragHandleProps : {})}>
      <input
        id={inputId}
        type="file"
        accept={`${mediaType}/*`}
        multiple={multiSelect}
        onChange={handleFileChange}
        className="hidden"
      />

      {filled && mediaType === "image" && (
        <img
          src={displayUrl}
          alt="asset"
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover pointer-events-none"
        />
      )}
      {filled &&
        mediaType === "video" &&
        (videoThumbnail ? (
          <img
            src={videoThumbnail}
            alt="asset"
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover pointer-events-none"
          />
        ) : (
          <video
            src={displayUrl}
            muted
            className="w-full h-full object-cover pointer-events-none"
          />
        ))}
      {filled && mediaType === "audio" && (
        <div className="w-full h-full flex items-center justify-center text-secondary-foreground/70 pointer-events-none">
          <LuAudioLines className="w-5 h-5" />
        </div>
      )}

      {!filled && <FiPlus className="w-5 h-5 text-secondary-foreground/60" />}

      {filled && (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={handleClear}
          className="absolute top-0.5 right-0.5 p-1 rounded-sm bg-background cursor-pointer text-secondary-foreground/80 hover:text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity outline-hidden focus:outline-hidden focus-visible:outline-hidden"
          title="Clear">
          <FiTrash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );

  // Filled pickers click straight through to preview; empty pickers open a
  // small menu offering Upload (local file picker) or Browse (pick an
  // existing asset without leaving the current folder).
  if (filled) return pickerSquare;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        {pickerSquare}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-32">
        <DropdownMenuItem className="text-xs" onSelect={handleBrowseClick}>
          <FiFolder className="w-3 h-3" />
          Browse
        </DropdownMenuItem>
        <DropdownMenuItem className="text-xs" onSelect={handleUploadClick}>
          <FiUpload className="w-3 h-3" />
          Upload
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SortableMiniAssetPicker({ id, ...pickerProps }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} className="group relative">
      <MiniAssetPicker
        {...pickerProps}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

export { SortableMiniAssetPicker };
export default MiniAssetPicker;
